/**
 * Turns a Better Auth client error into something a human can act on.
 *
 * Every sign-in failure used to render as "Invalid email or password." That
 * masked a real staging outage on 2026-08-18: the apex host `fraudefender.ai`
 * was served by the same ALB as the canonical `www.fraudefender.ai`, but Better
 * Auth trusts exactly ONE origin (`BETTER_AUTH_URL`), so every auth call from
 * the apex came back `403 INVALID_ORIGIN` in ~4ms — the password was never
 * checked. A tester spent the session convinced their credentials were wrong,
 * then tried a password reset that 403'd the same way and still showed
 * "Check your inbox", so nothing ever arrived and nothing said why.
 *
 * Only genuine credential rejection may say "invalid email or password"; that
 * one stays deliberately vague (never "no such user") so it cannot be used to
 * enumerate accounts. Everything else names what actually happened.
 */

/** The subset of Better Auth's client error we can rely on across versions. */
export interface AuthErrorLike {
  // Explicit `| undefined` (not just `?`) because the app builds with
  // `exactOptionalPropertyTypes`, and Better Auth's own error type declares
  // these as present-but-possibly-undefined.
  readonly status?: number | undefined;
  readonly code?: string | undefined;
  readonly message?: string | undefined;
}

export interface AuthErrorContext {
  /** `window.location.origin` of the page the user is acting from. */
  readonly currentOrigin: string;
  /** Origin the auth server trusts (`NEXT_PUBLIC_BETTER_AUTH_URL`); may be ''. */
  readonly canonicalOrigin: string;
  /** Path + query to carry over to the canonical host. Defaults to `/login`. */
  readonly currentPath?: string | undefined;
}

export interface AuthErrorDescription {
  readonly message: string;
  /**
   * Set only when the user is on the wrong host AND we know the right one, so
   * the form can offer a working link instead of a dead end.
   */
  readonly canonicalUrl?: string | undefined;
}

/** Back-compat alias — the sign-in form's original name for this shape. */
export type SignInErrorDescription = AuthErrorDescription;

const GENERIC_CREDENTIALS = 'Invalid email or password.';

/** Strip any trailing slash so origin comparison is not defeated by formatting. */
function normalizeOrigin(origin: string): string {
  return origin.replace(/\/+$/, '');
}

function canonicalAuthUrl(ctx: AuthErrorContext): string | undefined {
  const canonical = normalizeOrigin(ctx.canonicalOrigin);
  if (!canonical) return undefined;
  // Same host already — a link back to where they are helps nobody, and would
  // point at a genuinely misconfigured server rather than a user mistake.
  if (normalizeOrigin(ctx.currentOrigin) === canonical) return undefined;
  const path = ctx.currentPath && ctx.currentPath.startsWith('/') ? ctx.currentPath : '/login';
  return `${canonical}${path}`;
}

export interface AuthInfraFailureOptions {
  /**
   * Set on endpoints that must not reveal whether an account exists (password
   * reset). It drops the 5xx branch, because a server error there is NOT
   * account-independent: Better Auth answers an unknown address with a silent
   * success, so the only way to reach a 500 is for the account to exist and the
   * reset mail to fail on the way out. Reporting it would hand an attacker the
   * enumeration oracle the silent success exists to prevent.
   *
   * The origin and rate-limit branches stay on regardless — both are rejected
   * by middleware before any account lookup happens, so they carry no signal.
   */
  readonly enumerationSensitive?: boolean | undefined;
}

/**
 * Classifies the failures that are NOT about the user's account: a rejected
 * origin, a shared-IP rate limit, a server fault. Returns `null` for anything
 * whose outcome could depend on whether a given account exists.
 *
 * That split is what lets the password-reset form stay anti-enumeration
 * (it must answer identically for a real and a fake address) while still
 * refusing to claim "check your inbox" when the request never got through.
 */
export function describeAuthInfraFailure(
  error: AuthErrorLike,
  ctx: AuthErrorContext,
  opts: AuthInfraFailureOptions = {},
): AuthErrorDescription | null {
  if (error.code === 'INVALID_ORIGIN') {
    const canonicalUrl = canonicalAuthUrl(ctx);
    return canonicalUrl
      ? {
          message:
            "This address can't sign you in. Use the official address instead — your details are fine.",
          canonicalUrl,
        }
      : {
          message:
            "This site is being served from an address the login service doesn't recognise. Your details are fine — please report this.",
        };
  }

  if (error.status === 429) {
    return {
      message: 'Too many attempts from this network. Wait a minute, then try again.',
    };
  }

  if (!opts.enumerationSensitive && error.status !== undefined && error.status >= 500) {
    return {
      message: `The service is temporarily unavailable (server error ${error.status}). Please try again shortly.`,
    };
  }

  return null;
}

export function describeSignInError(
  error: AuthErrorLike,
  ctx: AuthErrorContext,
): AuthErrorDescription {
  const infra = describeAuthInfraFailure(error, ctx);
  if (infra) return infra;

  if (error.code === 'EMAIL_NOT_VERIFIED') {
    return { message: 'Verify your email address before signing in — check your inbox.' };
  }

  // The only case that may blame the credentials. Better Auth answers a bad
  // email OR a bad password with 401 / INVALID_EMAIL_OR_PASSWORD; an error with
  // no status at all (older clients, mocked tests) is treated the same way
  // rather than escalated to a scary platform message.
  if (
    error.status === 401 ||
    error.code === 'INVALID_EMAIL_OR_PASSWORD' ||
    error.status === undefined
  ) {
    return { message: GENERIC_CREDENTIALS };
  }

  return {
    message: `Sign-in was rejected (error ${error.status}). Your details are fine — please try again or report this.`,
  };
}
